<script setup lang="ts">
import { api } from '@/api';
import {
  type ResetPasswordRequestDto,
  ResetPasswordRequestDtoTypeEnum,
  ValidateTokenRequestDtoTypeEnum,
} from '@/api/generator';
import VeePasswordField from '@/components/forms/PasswordField/VeePasswordField.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import SimplePage from '@/components/pages/SimplePage/SimplePage.vue';
import PasswordQualityEstimation from '@/components/PasswordQualityEstimation/PasswordQualityEstimation.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { useZxcvbn } from '@/plugins/zxcvbn';
import { routenames } from '@/router/routenames.ts';
import { useFieldValue } from 'vee-validate';
import { onBeforeMount } from 'vue';
import { useRouter } from 'vue-router';

type ResetPasswordViewProps = {
  email: string;
  token: string;
};

const props = defineProps<ResetPasswordViewProps>();

const initialValues: Partial<ResetPasswordRequestDto> = {
  email: props.email,
  token: props.token,
  $type:
    ResetPasswordRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationAccountDtoResetPasswordRequestDtoPtwPtw100AppZeroApplicationShared,
};

const { form } = useVeeForm<ResetPasswordRequestDto>({
  schemaName: 'ResetPasswordRequestDto',
  options: {
    initialValues,
  },
  mode: 'edit',
});
const { handleSubmit, meta } = form;
const password = useFieldValue<string>('newPassword');
const router = useRouter();

const zxcvbnResult = useZxcvbn(password, [props.email]);

async function toForgotPasswordWithError() {
  await router.push({
    name: routenames.forgotPassword,
    query: {
      email: props.email,
      error: 'true',
    },
  });
}

onBeforeMount(async () => {
  const { result } = await api.account.validateResetPasswordToken({
    validateTokenRequestDto: {
      email: props.email,
      token: props.token,
      $type:
        ValidateTokenRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedAuthorizationAccountDtoValidateTokenRequestDtoPtwPtw100AppZeroApplicationShared,
    },
  });
  // if token is invalid, redirect to the forgot password page
  if (result?.result) return;
  await toForgotPasswordWithError();
});

const submit = handleSubmit(async (resetPasswordRequestDto) => {
  const { result } = await api.account.resetPassword(
    {
      resetPasswordRequestDto,
    },
    [formBackendErrorHandler(form)],
  );

  if (result?.result) {
    await router.push({ name: routenames.signOut });
  }
  // if meanwhile the token or user became invalid, then redirect to the forgot password page
  else if (result?.result === false) {
    await toForgotPasswordWithError();
  }
});
</script>

<template>
  <SimplePage :headline="$t('ResetPassword')">
    <p class="text-pre-line">
      {{ $t('ToResetAccountSetPasswordMsg') }}
    </p>
    <form class="form mt-6" @submit="submit">
      <!-- add hidden input for username according to
          https://www.chromium.org/developers/design-documents/form-styles-that-chromium-understands
          so that password managers can connect the new password to the correct username -->
      <input id="username" name="email" autocomplete="username" type="email" class="d-none" />
      <VeePasswordField
        :label="$t('NewPassword')"
        name="newPassword"
        :hint="$t('PasswordComplexityViolationMsg')"
        :persistentHint="true"
        autocomplete="new-password"
      />
      <PasswordQualityEstimation :result="zxcvbnResult" />
      <VeeSubmitButton
        class="mt-6"
        :disabled="!meta.valid"
        :block="true"
        :text="$t('ResetPassword')"
      />
    </form>
  </SimplePage>
</template>

<style lang="scss" scoped>
@use '@/styles/variables';
.form {
  max-width: variables.$maxSimpleLayoutFormWidth;
  margin: 0 auto;
  text-align: left;
}
</style>
